import * as React from 'react';
import { useSnackbar } from 'notistack';
import { useAppSelector } from '../../app/hooks';
import { Paper, Typography, useTheme } from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { ActivityTypeName } from '../../services';
import { isSnapshotCaptureFailed } from '../../helpers';

interface ThemedSnackbarProps {
    message: string;
    activityType?: string;
    /**
     * The activity's `snapshot_status`, as a plain string. Kept untyped here
     * because this component also renders non-activity messages, which carry
     * none.
     */
    snapshotStatus?: string;
}

// Renders inside the notistack content callback so it has access to the MUI
// theme. Paper uses background.paper which flips correctly in dark/light mode.
const ThemedSnackbar = React.forwardRef<HTMLDivElement, ThemedSnackbarProps>(
    ({ message, activityType, snapshotStatus }, ref) => {
        const theme = useTheme();

        let Icon = EditOutlinedIcon;
        let iconColor = theme.palette.info.main;
        if (activityType === ActivityTypeName.Create) {
            Icon = AddCircleOutlineIcon;
            iconColor = theme.palette.success.main;
        } else if (activityType === ActivityTypeName.Delete) {
            Icon = DeleteOutlineIcon;
            iconColor = theme.palette.error.main;
        }
        // Overrides the operation icon, deliberately: the noteworthy thing about
        // this message is no longer "a sale was updated" but "its history was
        // not recorded". The entity change still succeeded — nothing here says
        // otherwise, and the text says so explicitly.
        if (isSnapshotCaptureFailed(snapshotStatus)) {
            Icon = WarningAmberIcon;
            iconColor = theme.palette.warning.main;
        }

        return (
            <Paper
                ref={ref}
                elevation={6}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 2,
                    py: 1.25,
                    minWidth: 240,
                    maxWidth: 400,
                    bgcolor: 'background.paper',
                    color: 'text.primary',
                }}
            >
                <Icon fontSize="small" sx={{ color: iconColor, flexShrink: 0 }} />
                <Typography variant="body2" sx={{ flexGrow: 1 }}>
                    {message}
                </Typography>
            </Paper>
        );
    },
);

export const GlobalMessagesSnackbar = () => {
    const globalMessages = useAppSelector(
        (state) => state.globalMessages.value,
    );

    const { enqueueSnackbar } = useSnackbar();

    React.useEffect(() => {
        if (globalMessages.length > 0) {
            const { message, options } =
                globalMessages[globalMessages.length - 1];

            if (message !== '') {
                const activityType = options?.activityType;
                const snapshotStatus = options?.snapshotStatus;
                enqueueSnackbar(message, {
                    content: (_key) => (
                        <ThemedSnackbar
                            message={message}
                            activityType={activityType}
                            snapshotStatus={snapshotStatus}
                        />
                    ),
                });
            }
        }
    }, [globalMessages, enqueueSnackbar]);

    return null;
};
